import type {
  AIExtractionModel,
  AIExtractionMethod,
  AuthStateActionType,
  HttpMethod,
  SpecialLoginType,
} from '@prisma/client';
import { z } from 'zod';
import { isActionMethod, isAssertionMethod } from '~/ai_tools/playwright_utils/types';
import type { Position } from '~/common/recorder/recorder-operation';
import {
  isScrollDirection,
  zPosition,
  zScrollDirection,
  zScrollOperationValue,
  type ScrollDirection,
} from '~/common/recorder/recorder-operation';
import type { RelativeDragToData } from '~/components/test/steps/step/operation/dragTo/types';
import { zDragToRelativeData } from '~/components/test/steps/step/operation/dragTo/types';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type {
  OperationParentContext,
  EmailFilters,
  EmailVariableExtraction,
  FetchRequestHeaders,
  OperationData,
  ValueType,
} from '~/server/logic/steps/operation/types';
import type { BoundingBoxCoordinates, PlaywrightOperationOptions } from '~/types';
import type { BaseAction } from './parts/ActionPart';
import type { BaseAssertion } from './parts/AssertionPart';

export type ActionPart = {
  type: 'action';
  action?: BaseAction;
  options?: PlaywrightOperationOptions;
};

export type AssertionPart = {
  type: 'assertion';
  assertion?: BaseAssertion;
  invert?: boolean;
};

export type ValuePart = {
  type: 'value';
  valueType: ValueType;
  value: string;
};

export type TargetEntry = { name: string; locator: string; boundingBox?: BoundingBoxCoordinates | null };
export type TargetPart = {
  type: 'target';
  target: TargetEntry;
};

// For scroll operations that do not have a target, but want to still allow the user to edit it
export type EmptyTargetPart = {
  type: 'emptyTarget';
};

export type SelectedFile = {
  id: string;
  name: string;
};

export type UploadPart = {
  type: 'upload';
  files: SelectedFile[];
};

export type Slot = {
  type: 'slot';
};

export type DefineVariablePart = {
  type: 'defineVariable';
  name: string;
  value: string;
  valueType: ValueType;
  pageAlias?: string; // To specify which page the variable is extracted from
  includeScreenshot?: boolean;
  extractionModel?: AIExtractionModel;
  extractionMethod?: AIExtractionMethod;
};

export type ReceiveEmailPart = {
  type: 'receiveEmail';
  toEmailAddressValue: string;
  toEmailAddressType: ValueType;
  variableExtraction?: EmailVariableExtraction;
  filters?: EmailFilters;
};

export type FetchRequestPart = {
  type: 'fetchRequest';
  url: string;
  method: HttpMethod;
  headers: FetchRequestHeaders;
  body: string;
  responseVariableName: string;
};

export type AuthStatePart = {
  type: 'authState';
  actionType: AuthStateActionType;
  testAccountId: string;
};

export type SpecialLoginPart = {
  type: 'specialLogin';
  loginType: SpecialLoginType;
  testAccountId: string;
};

export type ComponentCallPart = {
  type: 'componentCall';
  componentId: string;
  // TODO: use proper zod type
  inputParameters: Record<string, { type: 'STATIC' | 'DYNAMIC'; value: string }>;
};

export type SelectDropdownPart = {
  type: 'selectDropdown';
  selectedOption: string;
  options: string[];
};

const zPositionOption = z.object({
  position: zPosition,
});

export type PositionOption = {
  type: 'position';
  position: Position;
};

export type TimeoutOption = {
  type: 'timeout';
  timeoutMs: number;
};

export type Part =
  | ActionPart
  | AssertionPart
  | ValuePart
  | TargetPart
  | UploadPart
  | SelectDropdownPart
  | Slot
  | DefineVariablePart
  | ReceiveEmailPart
  | FetchRequestPart
  | AuthStatePart
  | SpecialLoginPart
  | PositionOption
  | TimeoutOption
  | EmptyTargetPart
  | ComponentCallPart;

export const operationToParts = (operation: SelectedOperation): Part[] => {
  if (operation.type === 'ASSERTION' && operation.assertionOp) {
    // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const assertion: BaseAssertion = operation.assertionOp.assertion as BaseAssertion;

    return [
      { type: 'action', action: 'expect' },
      { type: 'slot' },
      ...(operation.assertionOp?.targetV2?.locator ?
        ([
          {
            type: 'target',
            target: {
              name: operation.assertionOp?.targetV2?.name || '',
              locator: operation.assertionOp?.targetV2?.locator || '',
              boundingBox: operation.assertionOp?.targetV2?.boundingBox || null,
            },
          },
          { type: 'slot' },
        ] as const)
      : []),
      { type: 'assertion', assertion, invert: operation.assertionOp.invert ?? undefined },
      ...(operation.assertionOp?.value ?
        ([
          {
            type: 'value',
            value: operation?.assertionOp?.value || '',
            valueType: operation?.assertionOp?.valueType || 'STATIC',
          },
          { type: 'slot' },
        ] as const)
      : []),
      ...(operation.assertionOp?.options?.timeout ?
        ([{ type: 'timeout', timeoutMs: operation.assertionOp.options.timeout }, { type: 'slot' }] as const)
      : []),
    ];
  } else if (operation.type === 'ACTION' && operation.actionOp) {
    if (operation.actionOp.action === 'scroll' && operation.actionOp.value) {
      const { direction, amount } = zScrollOperationValue.parse(JSON.parse(operation.actionOp.value));
      return [
        { type: 'action', action: 'scroll' },
        { type: 'slot' },
        {
          type: 'selectDropdown',
          selectedOption: direction,
          options: zScrollDirection.options,
        },
        { type: 'slot' },
        { type: 'value', value: amount.toString(), valueType: 'STATIC' },
        { type: 'slot' },
        ...(operation.actionOp.targetV2?.locator ?
          ([
            {
              type: 'target',
              target: {
                name: operation.actionOp.targetV2.name || '',
                locator: operation.actionOp.targetV2.locator || '',
                boundingBox: operation.actionOp.targetV2.boundingBox || null,
              },
            },
            { type: 'slot' },
          ] as const)
        : ([{ type: 'emptyTarget' }, { type: 'slot' }] as const)),
        ...(operation.actionOp.options?.timeout ?
          ([{ type: 'timeout', timeoutMs: operation.actionOp.options.timeout }, { type: 'slot' }] as const)
        : []),
      ];
    }

    const positionOptionParse = zPositionOption.safeParse(operation.actionOp.options);
    const position = positionOptionParse.data?.position;

    // For dragTo, we need to have two targets and the options do not have "position", so we have to do something more custom
    const isDragTo = operation.actionOp.action === 'dragTo';
    const relativeData =
      isDragTo ? zDragToRelativeData.safeParse(operation.actionOp.options).data?.relativeData : undefined;

    return [
      {
        type: 'action',
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        action: operation.actionOp.action as BaseAction,
        options: operation.actionOp.options ?? undefined,
      },
      { type: 'slot' },
      ...(operation.actionOp.targetV2?.locator ?
        ([
          {
            type: 'target',
            target: {
              name: operation.actionOp.targetV2.name,
              locator: operation.actionOp.targetV2.locator,
              boundingBox: operation.actionOp.targetV2.boundingBox || null,
            },
          },
          { type: 'slot' },
        ] as const)
      : []),
      ...(isDragTo && relativeData ?
        ([
          {
            type: 'position',
            position: relativeData.sourceRelativePos,
          },
          { type: 'slot' },
        ] as const)
      : []),
      ...(operation.actionOp.target2V2?.locator ?
        ([
          {
            type: 'target',
            target: {
              name: operation.actionOp.target2V2.name,
              locator: operation.actionOp.target2V2.locator,
              boundingBox: operation.actionOp.target2V2.boundingBox || null,
            },
          },
          { type: 'slot' },
        ] as const)
      : []),
      ...(isDragTo && relativeData ?
        ([
          {
            type: 'position',
            position: relativeData.targetRelativePos,
          },
          { type: 'slot' },
        ] as const)
      : []),
      ...(operation.actionOp.value ?
        ([
          { type: 'value', value: operation.actionOp.value, valueType: operation.actionOp.valueType || 'STATIC' },
          { type: 'slot' },
        ] as const)
      : []),
      ...(position ? ([{ type: 'position', position }, { type: 'slot' }] as const) : []),
      ...(operation.actionOp.options?.timeout ?
        ([{ type: 'timeout', timeoutMs: operation.actionOp.options.timeout }, { type: 'slot' }] as const)
      : []),
    ];
  } else if (operation.type === 'NAVIGATION' && operation.navigationOp) {
    return [
      { type: 'action', action: 'navigate' },
      { type: 'slot' },
      { type: 'value', value: operation.navigationOp.value, valueType: operation.navigationOp.valueType },
      { type: 'slot' },
      ...(operation.navigationOp.options?.timeout ?
        ([{ type: 'timeout', timeoutMs: operation.navigationOp.options.timeout }, { type: 'slot' }] as const)
      : []),
    ];
  } else if (operation.type === 'WAIT' && operation.waitOp) {
    const action = 'waitForTimeout';
    return [
      { type: 'action', action },
      { type: 'slot' },
      { type: 'value', value: operation.waitOp.timeoutInMillis.toString(), valueType: 'STATIC' },
      { type: 'slot' },
    ];
  } else if (operation.type === 'UPLOAD' && operation.uploadOp) {
    return [{ type: 'slot' }, { type: 'upload', files: operation.uploadOp.uploadedFiles }, { type: 'slot' }];
  } else if (operation.type === 'DEFINE_VARIABLE' && operation.defineVariableOp) {
    return [
      {
        type: 'defineVariable',
        name: operation.defineVariableOp.name,
        value: operation.defineVariableOp.value,
        valueType: operation.defineVariableOp.type,
        includeScreenshot: operation.defineVariableOp.includeScreenshot,
        extractionModel: operation.defineVariableOp.extractionModel,
        extractionMethod: operation.defineVariableOp.extractionMethod ?? undefined,
      } satisfies DefineVariablePart,
      { type: 'slot' },
    ];
  } else if (operation.type === 'RECEIVE_EMAIL' && operation.receiveEmailOp) {
    return [
      { type: 'slot' },
      {
        type: 'receiveEmail',
        toEmailAddressValue: operation.receiveEmailOp.toEmailAddressValue,
        toEmailAddressType: operation.receiveEmailOp.toEmailAddressType,
        variableExtraction: operation.receiveEmailOp.variableExtraction || undefined,
        filters: operation.receiveEmailOp.filters || undefined,
      } satisfies ReceiveEmailPart,
      { type: 'slot' },
    ];
  } else if (operation.type === 'FETCH_REQUEST' && operation.fetchRequestOp) {
    return [
      { type: 'slot' },
      {
        type: 'fetchRequest',
        url: operation.fetchRequestOp.url,
        method: operation.fetchRequestOp.method,
        headers: operation.fetchRequestOp.headers || {},
        body: operation.fetchRequestOp.body || '',
        responseVariableName: operation.fetchRequestOp.responseVariableName || '',
      } satisfies FetchRequestPart,
      { type: 'slot' },
    ];
  } else if (operation.type === 'AUTH_STATE' && operation.authStateOp) {
    return [
      { type: 'slot' },
      {
        type: 'authState',
        actionType: operation.authStateOp.actionType,
        testAccountId: operation.authStateOp.testAccountId,
      } satisfies AuthStatePart,
      { type: 'slot' },
    ];
  } else if (operation.type === 'SPECIAL_LOGIN' && operation.specialLoginOp) {
    return [
      { type: 'slot' },
      {
        type: 'specialLogin',
        loginType: operation.specialLoginOp.type,
        testAccountId: operation.specialLoginOp.testAccountId,
      } satisfies SpecialLoginPart,
      { type: 'slot' },
    ];
  } else if (operation.type === 'COMPONENT_CALL' && operation.componentCallOp) {
    return [
      { type: 'slot' },
      {
        type: 'componentCall',
        componentId: operation.componentCallOp.componentId,
        inputParameters: operation.componentCallOp.inputParameters || {},
      } satisfies ComponentCallPart,
      { type: 'slot' },
    ];
  } else if (operation.type === 'END_TEST' && operation.endTestOp) {
    return [
      { type: 'slot' },
      { type: 'action', action: 'endTest' },
      { type: 'slot' },
      {
        type: 'selectDropdown',
        selectedOption: operation.endTestOp.passTest ? 'success' : 'failure',
        options: ['success', 'failure'],
      },
    ];
  }
  // Invalid state
  return [];
};

export const partsToOperationData = ({
  stepId,
  operationId,
  order,
  parts,
  // see zOperation comment on why componentId is needed
  parentContext,
  pageAlias: currentPageAlias,
}: {
  stepId: string;
  operationId: string | undefined;
  order: number | undefined;
  parts: Part[];
  parentContext?: OperationParentContext;
  pageAlias?: string;
}): OperationData => {
  let name = '';
  let action = '';
  let assertion = { assertion: '', invert: false };
  let locator = '';
  let locatorName = '';
  let locator2 = '';
  let locatorName2 = '';
  let boundingBox: BoundingBoxCoordinates | null = null;
  let boundingBox2: BoundingBoxCoordinates | null = null;
  let value = '';
  let valueType: ValueType = 'STATIC';
  let isFirstTargetFound = false; // to know if dragTo first target is found
  let files: SelectedFile[] = [];
  let variableExtraction: EmailVariableExtraction | undefined;
  let scrollDirection: ScrollDirection = 'down';
  let defineVariablePageAlias: string | undefined;
  let filters: EmailFilters | undefined;
  let url: string | undefined;
  let method: HttpMethod | undefined;
  let headers: FetchRequestHeaders | undefined;
  let body: string | undefined;
  let responseVariableName: string | undefined;
  let position: Position | undefined;
  let includeScreenshot: boolean | undefined;
  let extractionModel: AIExtractionModel | undefined;
  let extractionMethod: AIExtractionMethod | undefined;
  let actionType: AuthStateActionType | undefined;
  // Used for both auth state and special login operations
  let testAccountId: string | undefined;
  // Used for both action and assertion operations
  let timeoutMs: number | undefined;
  let componentCallId: string | undefined;
  let inputParameters: Record<string, { type: 'STATIC' | 'DYNAMIC'; value: string }> | undefined;
  let loginType: SpecialLoginType | undefined;
  let passTest = true;

  // In case of dragTo
  let position2: Position | undefined;
  // Options for right-click, triple-click, etc.
  let existingActionOptions: PlaywrightOperationOptions | undefined;
  for (const p of parts) {
    if (p.type === 'action') {
      action = p.action || '';
      existingActionOptions = p.options;
    } else if (p.type === 'assertion') {
      assertion = {
        invert: p.invert ?? false,
        assertion: p.assertion || '',
      };
    } else if (p.type === 'target') {
      if (isFirstTargetFound) {
        locator2 = p.target.locator;
        locatorName2 = p.target.name;
        boundingBox2 = p.target.boundingBox ?? null;
      } else {
        ({ locator } = p.target);
        locatorName = p.target.name;
        boundingBox = p.target.boundingBox ?? null;
      }
      isFirstTargetFound = true;
    } else if (p.type === 'value') {
      ({ value } = p);
      ({ valueType } = p);
    } else if (p.type === 'selectDropdown') {
      if (action === 'scroll') {
        if (isScrollDirection(p.selectedOption)) {
          scrollDirection = p.selectedOption;
        }
      } else if (action === 'endTest') {
        passTest = p.selectedOption === 'success';
      }
    } else if (p.type === 'upload') {
      action = 'upload';
      ({ files } = p);
    } else if (p.type === 'defineVariable') {
      action = 'defineVariable';
      ({ name } = p);
      ({ value } = p);
      ({ valueType } = p);
      ({ pageAlias: defineVariablePageAlias } = p);
      ({ includeScreenshot } = p);
      ({ extractionModel } = p);
      ({ extractionMethod } = p);
    } else if (p.type === 'receiveEmail') {
      action = 'receiveEmail';
      value = p.toEmailAddressValue;
      valueType = p.toEmailAddressType;
      ({ variableExtraction } = p);
      ({ filters } = p);
    } else if (p.type === 'fetchRequest') {
      action = 'fetchRequest';
      ({ url } = p);
      ({ method } = p);
      ({ headers } = p);
      ({ body } = p);
      ({ responseVariableName } = p);
    } else if (p.type === 'authState') {
      action = 'authState';
      ({ actionType } = p);
      ({ testAccountId } = p);
    } else if (p.type === 'specialLogin') {
      action = 'specialLogin';
      ({ loginType } = p);
      ({ testAccountId } = p);
    } else if (p.type === 'componentCall') {
      action = 'componentCall';
      componentCallId = p.componentId;
      ({ inputParameters } = p);
    } else if (p.type === 'position') {
      if (!position) {
        ({ position } = p);
      } else {
        position2 = p.position;
      }
    } else if (p.type === 'timeout') {
      ({ timeoutMs } = p);
    }
  }

  const operation =
    action === 'navigate' ?
      ({
        operationType: 'NAVIGATION',
        value,
        valueType,
      } as const)
    : action === 'waitForTimeout' ?
      ({
        operationType: 'WAIT',
        type: 'TIMEOUT',
        timeoutInMillis: parseInt(value),
      } as const)
    : isActionMethod(action) ?
      ({
        operationType: 'ACTION',
        action,
        target: locator ? { name: locatorName, locator, ...(boundingBox ? { boundingBox } : {}) } : undefined,
        target2:
          action === 'dragTo' ?
            { name: locatorName2, locator: locator2, ...(boundingBox2 ? { boundingBox: boundingBox2 } : {}) }
          : undefined,
        value:
          action === 'scroll' ?
            JSON.stringify({
              direction: scrollDirection,
              amount: Math.abs(Number(value)),
            } satisfies z.output<typeof zScrollOperationValue>)
          : value,
        valueType,
        options: {
          ...existingActionOptions,
          ...(position && position2 ?
            ({
              relativeData: {
                sourceRelativePos: position,
                targetRelativePos: position2,
              },
            } satisfies RelativeDragToData)
          : position ? { position }
          : {}),
          ...(timeoutMs ? { timeout: timeoutMs } : {}),
        },
      } as const)
    : action === 'expect' && isAssertionMethod(assertion.assertion) ?
      ({
        operationType: 'ASSERTION',
        assertion: assertion.assertion,
        target: { name: locatorName, locator, ...(boundingBox ? { boundingBox } : {}) },
        invert: assertion.invert,
        value,
        valueType,
        options: timeoutMs ? { timeout: timeoutMs } : {},
      } as const)
      // TODO: properly support code block
    : action === 'upload' ?
      ({
        operationType: 'UPLOAD',
        // It's safe to pass an empty url here, as it is not used
        // by the api
        files: files.map((file) => ({ ...file, storageKey: '' })),
      } as const)
    : action === 'defineVariable' ?
      ({
        operationType: 'DEFINE_VARIABLE',
        name,
        value,
        type: valueType,
        includeScreenshot,
        extractionModel,
        extractionMethod,
      } as const)
    : action === 'receiveEmail' ?
      ({
        operationType: 'RECEIVE_EMAIL',
        toEmailAddressValue: value,
        toEmailAddressType: valueType,
        variableExtraction,
        filters,
      } as const)
    : action === 'fetchRequest' ?
      ({
        operationType: 'FETCH_REQUEST',
        responseVariableName,
        request: {
          url: url || '',
          method: method || ('GET' satisfies HttpMethod),
          headers,
          body,
        },
      } as const)
    : action === 'authState' ?
      ({
        operationType: 'AUTH_STATE',
        actionType: actionType || ('LOAD' satisfies AuthStateActionType),
        testAccountId: testAccountId || '',
      } as const)
    : action === 'componentCall' ?
      ({
        operationType: 'COMPONENT_CALL',
        componentId: componentCallId || '',
        inputParameters: inputParameters || undefined,
      } as const)
    : action === 'specialLogin' ?
      ({
        operationType: 'SPECIAL_LOGIN',
        type: loginType || 'GOOGLE',
        testAccountId: testAccountId || '',
      } as const)
    : action === 'endTest' ?
      ({
        operationType: 'END_TEST',
        passTest,
      } as const)
    : ({
        operationType: 'CODE_BLOCK',
        code: '',
      } as const);

  return {
    operation,
    pageAlias: defineVariablePageAlias || currentPageAlias || 'page',
    stepId,
    operationId,
    order,
    parentContext,
  };
};
